import { API_URL } from "../api";

const PAYMENT_LABELS = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
  credit: "Crédito",
};


function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}


function money(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}


function quantity(value) {
  return new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: 3,
  }).format(Number(value || 0));
}


function dateTime(value) {
  if (!value) return "No disponible";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "No disponible";
  }

  return date.toLocaleString("es-CO", {
    timeZone: "America/Bogota",
  });
}


function getApiBase() {
  return String(API_URL || "").replace(/\/+$/, "");
}


function publicUrl(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  const path = value.trim();

  if (/^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(path)) {
    return path;
  }

  try {
    const backend = new URL(
      `${getApiBase()}/`,
      window.location.origin
    );

    const url = new URL(path, backend);

    return ["http:", "https:"].includes(url.protocol)
      ? url.href
      : "";
  } catch {
    return "";
  }
}


function getCompany(report, options) {
  if (options.company) {
    return String(options.company);
  }

  if (report.subdomain || report.company_subdomain) {
    return String(
      report.subdomain || report.company_subdomain
    );
  }

  try {
    return decodeURIComponent(
      window.location.pathname
        .split("/")
        .filter(Boolean)[0] || ""
    );
  } catch {
    return "";
  }
}


async function getBranding(report, options) {
  const company = getCompany(report, options);
  const suppliedBranding = options.branding || {};

  const fallback =
    !suppliedBranding.subdomain ||
    suppliedBranding.subdomain === company
      ? suppliedBranding
      : {};

  if (!company) return fallback;

  const controller = new AbortController();

  const timer = window.setTimeout(
    () => controller.abort(),
    8000
  );

  try {
    const response = await fetch(
      `${getApiBase()}/companies/by-subdomain/${
        encodeURIComponent(company)
      }/branding`,
      {
        signal: controller.signal,
        credentials: "omit",
      }
    );

    if (!response.ok) {
      throw new Error("No fue posible consultar la marca.");
    }

    const data = await response.json();

    if (
      !data ||
      typeof data !== "object" ||
      Array.isArray(data)
    ) {
      throw new Error("Respuesta de marca inválida.");
    }

    if (data.subdomain && data.subdomain !== company) {
      throw new Error("La marca corresponde a otra empresa.");
    }

    return data;
  } catch {
    return fallback;
  } finally {
    window.clearTimeout(timer);
  }
}


function waitForLogo(outputWindow) {
  return new Promise((resolve) => {
    const logo = outputWindow.document.querySelector(
      ".receipt-logo"
    );

    if (!logo) {
      resolve();
      return;
    }

    let finished = false;
    let timer;

    function finish() {
      if (finished) return;

      finished = true;

      window.clearTimeout(timer);

      logo.removeEventListener("load", finish);
      logo.removeEventListener("error", fail);

      resolve();
    }

    function fail() {
      logo.hidden = true;
      finish();
    }

    timer = window.setTimeout(fail, 8000);

    logo.addEventListener("load", finish);
    logo.addEventListener("error", fail);

    if (logo.complete) {
      if (logo.naturalWidth > 0) {
        finish();
      } else {
        fail();
      }
    }
  });
}


function row(label, amount, strong = false) {
  return `
    <div class="receipt-row ${strong ? "strong" : ""}">
      <span>${escapeHtml(label)}</span>
      <span>${money(amount)}</span>
    </div>
  `;
}


function quantityRow(label, value, strong = false) {
  return `
    <div class="receipt-row ${strong ? "strong" : ""}">
      <span>${escapeHtml(label)}</span>
      <span>${quantity(value)}</span>
    </div>
  `;
}


export function openCashClosingPrintWindow(
  report,
  options = {}
) {
  if (!report) return false;

  const outputWindow = window.open(
    "",
    "_blank",
    "width=460,height=800"
  );

  if (!outputWindow) return false;

  const paperWidth =
    Number(options.paperWidth) === 58 ? 58 : 80;

  outputWindow.document.open();

  outputWindow.document.write(`
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Preparando cierre</title>
      </head>

      <body>
        Preparando comprobante…
      </body>
    </html>
  `);

  outputWindow.document.close();

  async function prepare() {
    const branding = await getBranding(report, options);

    if (outputWindow.closed) return;

    const companyName =
      branding.commercial_name ||
      branding.company_name ||
      report.company_name ||
      "Empresa";

    const logo = publicUrl(
      branding.logo || report.company_logo
    );

    // Los importes almacenados incluyen el saldo de apertura.
    const openingAmount = Number(
      report.opening_amount || 0
    );

    const expectedWithoutOpening = Number(
      (
        Number(report.expected_amount || 0) -
        openingAmount
      ).toFixed(2)
    );

    const countedWithoutOpening = Number(
      (
        Number(report.counted_amount || 0) -
        openingAmount
      ).toFixed(2)
    );

    const difference = Number(
      report.difference_amount || 0
    );

    const differenceLabel =
      difference === 0
        ? "Caja cuadrada"
        : difference > 0
          ? "Sobrante"
          : "Faltante";

    const paymentItems = Array.isArray(report.sales_by_method)
      ? report.sales_by_method
      : [];

    const productItems = Array.isArray(report.sold_products)
      ? report.sold_products
      : [];

    const refundItems = Array.isArray(report.refunds_by_method)
      ? report.refunds_by_method
      : [];

    const hasProductDetails = Array.isArray(
      report.sold_products
    );

    const payments = paymentItems
      .map((item) =>
        row(
          item.label ||
            PAYMENT_LABELS[item.payment_method] ||
            item.payment_method ||
            "Otro medio",
          item.amount
        )
      )
      .join("");

    // Los productos ya llegan consolidados desde el backend.
    // Aquí se organizan bajo el encabezado de su categoría.
    const productsByCategory = new Map();

    productItems.forEach((item) => {
      const category =
        String(item.category || "").trim() || "Sin categoría";

      if (!productsByCategory.has(category)) {
        productsByCategory.set(category, []);
      }

      productsByCategory.get(category).push(item);
    });

    const products = [...productsByCategory.entries()]
      .sort(([categoryA], [categoryB]) =>
        categoryA.localeCompare(categoryB, "es", {
          sensitivity: "base",
          numeric: true,
        })
      )
      .map(([category, items]) => {
        const sortedItems = [...items].sort((a, b) =>
          String(a.product_name || "").localeCompare(
            String(b.product_name || ""),
            "es",
            {
              sensitivity: "base",
              numeric: true,
            }
          )
        );

        const categoryUnits = items.reduce(
          (total, item) =>
            total + Number(item.quantity || 0),
          0
        );

        const categoryTotal = items.reduce(
          (total, item) =>
            total + Number(item.total || 0),
          0
        );

        const productRows = sortedItems
          .map((item) => `
            <tbody class="product-group">
              <tr>
                <td colspan="2" class="product-name">
                  <strong>
                    ${escapeHtml(item.product_name)}
                  </strong>

                  <small>
                    SKU: ${escapeHtml(item.sku || "Sin código")}
                    ${
                      item.variant_id != null
                        ? ` · Variante #${escapeHtml(item.variant_id)}`
                        : ""
                    }
                  </small>
                </td>
              </tr>

              <tr>
                <td>
                  ${quantity(item.quantity)} unidad(es)
                </td>

                <td class="right">
                  ${money(item.total)}
                </td>
              </tr>
            </tbody>
          `)
          .join("");

        return `
          <section class="category-section">
            <div class="category-heading">
              <small>CATEGORÍA</small>
              <h4>${escapeHtml(category)}</h4>
            </div>

            <table>
              ${productRows}
            </table>

            <div class="category-summary">
              ${quantityRow(
                "Unidades de la categoría",
                categoryUnits
              )}

              ${row(
                "Subtotal de la categoría",
                categoryTotal,
                true
              )}
            </div>
          </section>
        `;
      })
      .join("");

    const refunds = refundItems
      .map((item) =>
        row(
          item.label ||
            PAYMENT_LABELS[item.payment_method] ||
            item.payment_method ||
            "Otro medio",
          item.amount
        )
      )
      .join("");

    const warningItems = Array.isArray(report.report_warnings)
      ? [...report.report_warnings]
      : [];

    if (!hasProductDetails) {
      warningItems.push(
        "No se recibió el detalle de productos vendidos. " +
        "Consulta nuevamente el reporte del cierre."
      );
    }

    const warnings = warningItems
      .map((message) => `
        <p>${escapeHtml(message)}</p>
      `)
      .join("");

    const soldUnits =
      report.sold_units ??
      productItems.reduce(
        (total, item) =>
          total + Number(item.quantity || 0),
        0
      );

    const soldProductsTotal =
      report.sold_products_total ??
      productItems.reduce(
        (total, item) =>
          total + Number(item.total || 0),
        0
      );

    outputWindow.document.open();

    outputWindow.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1"
          />

          <title>
            Cierre #${escapeHtml(report.session_id)}
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              width: ${paperWidth}mm;
              margin: 0;
              color: #000;
              background: #fff;
              font-family: Arial, Helvetica, sans-serif;
              font-size: ${paperWidth === 58 ? 10 : 11}px;
            }

            .receipt {
              padding: 4mm;
              overflow-wrap: anywhere;
            }

            .center {
              text-align: center;
            }

            .receipt-logo {
              display: block;
              max-width: ${paperWidth === 58 ? 30 : 40}mm;
              max-height: 24mm;
              margin: 0 auto 3mm;
              object-fit: contain;
            }

            .receipt-logo[hidden] {
              display: none;
            }

            h1 {
              margin: 0 0 2mm;
              font-size: 16px;
            }

            h2 {
              margin: 2mm 0;
              font-size: 13px;
            }

            h3 {
              margin: 0 0 3mm;
              font-size: 11px;
              text-transform: uppercase;
              break-after: avoid;
            }

            p {
              margin: 1mm 0;
              line-height: 1.4;
            }

            small {
              font-size: 9px;
            }

            .section {
              margin-top: 4mm;
              padding-top: 3mm;
              border-top: 1px dashed #555;
            }

            .receipt-row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 3mm;
              margin: 2mm 0;
              break-inside: avoid;
            }

            .receipt-row > :first-child {
              min-width: 0;
            }

            .receipt-row > :last-child {
              flex-shrink: 0;
              text-align: right;
            }

            .strong {
              font-weight: 700;
            }

            .highlight {
              margin-top: 2mm;
              padding-top: 2mm;
              border-top: 1px solid #000;
              break-inside: avoid;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              padding: 1.5mm 0;
              text-align: left;
              vertical-align: top;
            }

            th {
              font-size: 9px;
            }

            thead {
              display: table-header-group;
            }

            tr,
            .product-group {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .right {
              text-align: right;
              white-space: nowrap;
            }

            .product-name {
              padding-top: 3mm;
              border-top: 1px dotted #aaa;
            }

            .product-name small {
              display: block;
              margin-top: 1mm;
            }

            .category-section {
              margin-top: 4mm;
            }

            .category-heading {
              padding: 2mm 0;
              border-top: 2px solid #000;
              border-bottom: 1px solid #000;
              break-inside: avoid;
              break-after: avoid;
              page-break-after: avoid;
            }

            .category-heading small {
              display: block;
              font-size: 8px;
              letter-spacing: 0.08em;
            }

            .category-heading h4 {
              margin: 1mm 0 0;
              font-size: 12px;
              font-weight: 800;
            }

            .category-summary {
              margin-top: 2mm;
              padding-top: 1mm;
              border-top: 1px dashed #555;
              break-inside: avoid;
            }

            .category-summary .receipt-row {
              margin: 1.5mm 0;
            }

            .category-section .product-name {
              padding-top: 2mm;
            }

            .category-section table > tbody:first-child .product-name {
              border-top: none;
            }

            .signature {
              margin-top: 10mm;
              padding-top: 2mm;
              border-top: 1px solid #000;
              text-align: center;
              break-inside: avoid;
            }

            .note {
              white-space: pre-wrap;
            }

            @page {
              margin: 0;
            }

            @media print {
              body {
                margin: 0;
              }
            }
          </style>
        </head>

        <body>
          <main class="receipt">
            <header class="center">
              ${
                logo
                  ? `
                    <img
                      class="receipt-logo"
                      src="${escapeHtml(logo)}"
                      alt="Logo de ${escapeHtml(companyName)}"
                    />
                  `
                  : ""
              }

              <h1>${escapeHtml(companyName)}</h1>

              ${
                report.company_nit
                  ? `
                    <p>
                      NIT: ${escapeHtml(report.company_nit)}
                    </p>
                  `
                  : ""
              }

              ${
                report.company_address
                  ? `
                    <p>
                      ${escapeHtml(report.company_address)}
                    </p>
                  `
                  : ""
              }

              <p>
                ${escapeHtml(
                  [
                    report.company_phone,
                    report.company_email,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                )}
              </p>

              <h2>CIERRE Y ARQUEO DE CAJA</h2>

              <p>
                Comprobante #${escapeHtml(report.session_id)}
              </p>
            </header>

            <section class="section">
              <p>
                <strong>Abierta por:</strong>
                ${escapeHtml(report.opened_by || "Sistema")}
              </p>

              <p>
                ${escapeHtml(dateTime(report.opened_at))}
              </p>

              <p>
                <strong>Cerrada por:</strong>
                ${escapeHtml(report.closed_by || "Sistema")}
              </p>

              <p>
                ${escapeHtml(dateTime(report.closed_at))}
              </p>

              <p>
                <small>Alcance: esta sesión de caja.</small>
              </p>
            </section>

            <section class="section">
              <h3>Base inicial · no es una venta</h3>

              ${row(
                "Base de apertura separada",
                openingAmount,
                true
              )}
            </section>

            <section class="section">
              <h3>Ventas por medio de pago</h3>

              <p>
                Ventas registradas:
                ${
                  report.sales_count != null
                    ? escapeHtml(report.sales_count)
                    : "No disponible"
                }
              </p>

              ${
                payments ||
                "<p>No hay detalle de pagos disponible.</p>"
              }

              <div class="highlight">
                ${
                  report.sales_total != null
                    ? row(
                        "Total de ventas",
                        report.sales_total,
                        true
                      )
                    : "<p>Total de ventas no disponible.</p>"
                }
              </div>

              <p>
                <small>
                  Excluye base inicial e ingresos manuales.
                  Crédito representa una cuenta por cobrar.
                  Devoluciones presentadas por separado.
                </small>
              </p>
            </section>

            <section class="section">
              <h3>Productos vendidos por categoría</h3>

              <div class="products-by-category">
                ${
                  products ||
                  `<p>${
                    hasProductDetails
                      ? "Sin productos vendidos."
                      : "Detalle de productos no disponible."
                  }</p>`
                }
              </div>

              ${
                hasProductDetails
                  ? `
                    <div class="highlight">
                      ${quantityRow(
                        "Unidades vendidas",
                        soldUnits
                      )}

                      ${row(
                        "Total de productos",
                        soldProductsTotal,
                        true
                      )}
                    </div>
                  `
                  : ""
              }

              <p>
                <small>
                  Cantidades e importes antes de devoluciones.
                  Los subtotales por categoría suman los valores
                  de las líneas con descuentos e impuestos
                  registrados.
                </small>
              </p>
            </section>

            ${
              refunds
                ? `
                  <section class="section">
                    <h3>
                      Devoluciones registradas en esta caja
                    </h3>

                    ${refunds}

                    <p>
                      <small>
                        Pueden corresponder a ventas
                        de otra sesión.
                      </small>
                    </p>
                  </section>
                `
                : ""
            }

            <section class="section">
              <h3>Arqueo de efectivo</h3>

              ${row(
                "Base inicial separada",
                openingAmount
              )}

              ${row(
                "Ventas en efectivo de caja",
                report.sales_income
              )}

              ${row(
                "Ingresos manuales / abonos",
                report.manual_income
              )}

              ${row(
                "Egresos manuales",
                -Number(report.manual_expense || 0)
              )}

              ${row(
                "Reintegros en efectivo",
                -Number(report.refunds || 0)
              )}

              <div class="highlight">
                ${row(
                  "Esperado sin base inicial",
                  expectedWithoutOpening,
                  true
                )}

                ${row(
                  "Contado sin base inicial",
                  countedWithoutOpening,
                  true
                )}

                ${row(
                  differenceLabel,
                  Math.abs(difference),
                  true
                )}
              </div>

              ${row(
                "Efectivo físico contado, incluida base",
                report.counted_amount
              )}

              <p>
                <small>
                  El cuadre compara valores sin la base inicial.
                  El total físico contado incluye el saldo
                  de apertura. Tarjeta, transferencia y
                  crédito se presentan aparte.
                </small>
              </p>
            </section>

            ${
              warnings
                ? `
                  <section class="section">
                    <h3>Observaciones de conciliación</h3>
                    ${warnings}
                  </section>
                `
                : ""
            }

            ${
              report.closing_note
                ? `
                  <section class="section">
                    <h3>Observación de cierre</h3>

                    <p class="note">${escapeHtml(
                      report.closing_note
                    )}</p>
                  </section>
                `
                : ""
            }

            <div class="signature">
              Cajero: ${escapeHtml(report.closed_by || "")}
            </div>

            <div class="signature">
              Supervisor
            </div>

            <footer class="section center">
              <p>NetLedger · SYNTRIXA S.A.S.</p>

              <small>
                Documento de control interno
              </small>
            </footer>
          </main>
        </body>
      </html>
    `);

    outputWindow.document.close();

    await waitForLogo(outputWindow);

    if (outputWindow.closed) return;

    if (outputWindow.document.fonts?.ready) {
      await outputWindow.document.fonts.ready;
    }

    if (outputWindow.closed) return;

    window.setTimeout(() => {
      if (!outputWindow.closed) {
        outputWindow.focus();
        outputWindow.print();
      }
    }, 300);
  }

  prepare().catch(() => {
    if (!outputWindow.closed) {
      outputWindow.document.body.textContent =
        "No fue posible preparar el comprobante. " +
        "El cierre guardado no se ha modificado. " +
        "Cierra esta ventana e intenta imprimir nuevamente.";
    }
  });

  return true;
}